import pdfMake from 'pdfmake/build/pdfmake';
import { ReportDetails } from '../types';
import { Project } from '../types';
import { createReportTemplate, ReportPdfData } from './pdfReportTemplate';
import {
  getMetarouterLogoDataUrl,
  getMetarouterMarkDataUrl,
  getArchitectLogoDataUrl,
  fontData,
  preloadFonts
} from './pdfAssets';
import env from './env';

// Preload assets for use in PDFs
let metarouterLogoDataUrl: string = '';
let metarouterMarkDataUrl: string = '';
let architectLogoDataUrl: string = '';

/**
 * Convert an image URL to a base64 data URL
 * @param url The URL of the image to convert
 * @returns A Promise that resolves to a base64 data URL
 */
const convertImageUrlToBase64 = async (url: string): Promise<string> => {
  try {
    // Skip if already a data URL
    if (url.startsWith('data:')) {
      return url;
    }

    // Create a canvas to draw the image and convert to base64
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous'; // Enable CORS
      
      img.onload = () => {
        // Create a canvas element
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        
        // Draw the image on the canvas
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }
        
        ctx.drawImage(img, 0, 0);
        
        // Convert to base64
        try {
          const dataUrl = canvas.toDataURL();
          resolve(dataUrl);
        } catch (err) {
          console.error('Error converting canvas to data URL:', err);
          reject(err);
        }
      };
      
      img.onerror = (err) => {
        console.error('Error loading image:', err);
        reject(new Error(`Failed to load image from ${url}`));
      };
      
      // Set the source to start loading
      img.src = url;
    });
  } catch (error) {
    console.error('Error converting image to base64:', error);
    return ''; // Return empty string on error
  }
};

// Function to ensure assets are loaded before generating PDFs
const ensureAssetsLoaded = async (): Promise<void> => {
  if (!metarouterLogoDataUrl || !metarouterMarkDataUrl) {
    metarouterLogoDataUrl = await getMetarouterLogoDataUrl();
    metarouterMarkDataUrl = await getMetarouterMarkDataUrl();
    architectLogoDataUrl = await getArchitectLogoDataUrl();
  }
  
  if (!fontData.regular) {
    const fontsLoaded = await preloadFonts();
    if (!fontsLoaded) {
      console.error('Failed to load fonts for PDF generation');
      throw new Error('Failed to load fonts for PDF generation');
    }
  }
};

// Initialize pdfMake with custom fonts
const initPdfMake = async () => {
  try {
    // Ensure fonts are loaded
    await ensureAssetsLoaded();
    
    // Verify font data is loaded
    if (!fontData.regular || !fontData.bold) {
      console.error('Font data not loaded properly:', {
        regularLoaded: !!fontData.regular,
        boldLoaded: !!fontData.bold,
        mediumLoaded: !!fontData.medium,
        semiBoldLoaded: !!fontData.semiBold
      });
      throw new Error('Font data not loaded properly');
    }
    
    // Create the virtual file system with font data
    const vfs = {
      'IBMPlexSans-Regular.ttf': fontData.regular,
      'IBMPlexSans-Bold.ttf': fontData.bold,
      'IBMPlexSans-Medium.ttf': fontData.medium,
      'IBMPlexSans-SemiBold.ttf': fontData.semiBold,
    };
    // Set up pdfMake with our fonts
    pdfMake.vfs = vfs;
    
    // Define the fonts
    pdfMake.fonts = {
      IBMPlexSans: {
        normal: 'IBMPlexSans-Regular.ttf',
        bold: 'IBMPlexSans-Bold.ttf',
        italics: 'IBMPlexSans-Regular.ttf',
        bolditalics: 'IBMPlexSans-Bold.ttf'
      }
    };

    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Prepares the report data from project and report details
 * @param project The project data
 * @param reportDetails The report details
 * @returns The prepared report data
 */
const prepareReportData = async (project: Project, reportDetails: ReportDetails): Promise<ReportPdfData> => {
  // Get all integrations to map IDs to names
  let integrations: any[] = [];
  try {
    const response = await fetch(`${env.apiUrl}/integrations`);
    if (response.ok) {
      const data = await response.json();
      integrations = data.data || [];
    }
  } catch (error) {
    console.error('Error fetching integrations:', error);
  }

  // Create a map of integration IDs to names
  const integrationMap = new Map();
  integrations.forEach(integration => {
    integrationMap.set(integration.id, integration.name);
  });

  // Extract node information for architecture section with all node-specific fields
  const architectureNodes = project.nodes.map(node => {
    // Start with basic node information
    const nodeData: any = {
      type: node.type || 'unknown',
      label: node.data.label || 'Unnamed Node',
      description: node.data.description || ''
    };
    
    // Add all node-specific fields based on node type
    if (node.type === 'cluster') {
      // Cluster-specific fields
      const clusterData = node.data as any;
      nodeData.environment = clusterData.clusterEnvironment?.name;
      
      // Filter out undefined modules and only include enabled ones
      if (clusterData.modules && Array.isArray(clusterData.modules)) {
        nodeData.modules = clusterData.modules.filter((module: any) => 
          module && module.name && module.enabled
        );
      } else {
        nodeData.modules = [];
      }
      
      nodeData.teamAccess = clusterData.teamAccess;
      nodeData.customLabel = clusterData.customLabel;
      nodeData.totalLatencyImpact = clusterData.totalLatencyImpact;
    } else if (node.type === 'sync-injector') {
      // Sync Injector specific fields
      const syncInjectorData = node.data as any;
      nodeData.domains = syncInjectorData.domains;
      nodeData.crossDomain = syncInjectorData.crossDomain;
      
      // Map ID Syncs UUIDs to integration names
      if (syncInjectorData.idSyncs && Array.isArray(syncInjectorData.idSyncs)) {
        nodeData.idSyncs = syncInjectorData.idSyncs.map((id: string) => 
          integrationMap.get(id) || id
        );
      } else {
        nodeData.idSyncs = [];
      }
    } else if (node.type === 'load-balancer') {
      // Load Balancer specific fields
      const loadBalancerData = node.data as any;
      nodeData.strategy = loadBalancerData.strategy;
      nodeData.customLabel = loadBalancerData.customLabel;
      nodeData.status = loadBalancerData.status;
    } else if (node.type === 'javascript-source' || 
               node.type === 'mobile-sdk' || node.type === 'server-source') {
      // Source node specific fields
      const sourceData = node.data as any;
      nodeData.url = sourceData.url;
      nodeData.eventVolume = sourceData.eventVolume;
      nodeData.syncInjectorId = sourceData.syncInjectorId;
      nodeData.eventSpec = sourceData.eventSpec;
      nodeData.consentTool = sourceData.consentTool;
      nodeData.otherConsentToolName = sourceData.otherConsentToolName;
      nodeData.latencyImpact = sourceData.latencyImpact;
      nodeData.status = sourceData.status;
    } else if (node.type === 'pipeline') {
      // Pipeline specific fields
      const pipelineData = node.data as any;
      nodeData.writekey = pipelineData.writekey;
      nodeData.modules = pipelineData.modules;
      nodeData.idSyncs = pipelineData.idSyncs;
      nodeData.name = pipelineData.name;
      nodeData.notes = pipelineData.notes;
    } else if (node.type === 'filter') {
      // Filter specific fields
      const filterData = node.data as any;
      nodeData.filterType = filterData.filterType;
      nodeData.name = filterData.name;
      nodeData.notes = filterData.notes;
    } else if (node.type === 'event') {
      // Event specific fields
      const eventData = node.data as any;
      nodeData.notes = eventData.notes;
    }
    
    return nodeData;
  });

  // Extract module information
  const modules = project.nodes
    .filter(node => node.type === 'cluster')
    .flatMap(node => {
      // Cast to ClusterNodeData to access modules property
      const clusterData = node.data as any;
      if (!clusterData.modules) return [];
      return clusterData.modules.map((module: any) => ({
        name: module.name || 'Unnamed Module',
        description: module.description || 'No description',
        enabled: module.enabled || false
      }));
    });

  // Extract destination information with icons and documentation links
  // Group by integration to show all nodes using the same integration
  const destinationsByIntegration: Record<string, any> = {};
  
  // First, collect all destination nodes
  const destinationNodes = project.nodes.filter(node => node.type === 'destination');
  
  // Process each destination node
  for (const node of destinationNodes) {
    // Cast to DestinationNodeData to access properties
    const destData = node.data as any;
    const integrationName = destData.name || node.data.label || 'Unnamed Integration';
    const integrationKey = `${integrationName}-${destData.category || 'Other'}`;
    
    // Create a node representation with all its fields
    const nodeRepresentation = {
      id: node.id,
      label: node.data.label || 'Unnamed Node',
      status: node.data.status,
      customLabel: destData.customLabel,
      teamAccess: destData.teamAccess,
      latencyImpact: destData.latencyImpact,
      schema: destData.schema
    };
    
    // If this integration doesn't exist yet, create it
    if (!destinationsByIntegration[integrationKey]) {
      // Convert icon URL to base64 if it exists
      let iconBase64: string | undefined = undefined;
      if (destData.icon) {
        try {
          const base64Result = await convertImageUrlToBase64(destData.icon);
          if (base64Result) {
            iconBase64 = base64Result;
          }
        } catch (error) {
          console.error(`Error converting icon to base64 for ${integrationName}:`, error);
        }
      }
      
      destinationsByIntegration[integrationKey] = {
        name: integrationName,
        category: destData.category || 'Other',
        description: destData.description || '',
        icon: iconBase64,
        docs: destData.docs || undefined,
        supportsSyncInjector: destData.sync_injector !== undefined ? destData.sync_injector : true,
        nodes: [nodeRepresentation]
      };
    } else {
      // Add this node to the existing integration
      destinationsByIntegration[integrationKey].nodes.push(nodeRepresentation);
    }
  }
  
  // Convert the record to an array
  const destinations = Object.values(destinationsByIntegration);

  // Prepare the report data
  return {
    // Basic report details
    title: reportDetails.title,
    authorName: reportDetails.authorName,
    ownerRole: reportDetails.ownerRole,
    authorEmail: reportDetails.authorEmail,
    selectedSections: reportDetails.selectedSections,
    salesContact: reportDetails.salesContact,
    salesContactInfo: reportDetails.salesContactInfo && {
      id: reportDetails.salesContact, // Use salesContact as id
      name: reportDetails.salesContactInfo.name,
      email: reportDetails.salesContactInfo.email,
      role: reportDetails.salesContactInfo.role,
      phone: reportDetails.salesContactInfo.phone,
      photo_url: reportDetails.salesContactInfo.photo_url
    },
    
    // Project details
    projectId: project.id,
    projectName: project.name,
    projectDescription: project.description,
    createdAt: project.createdAt,
    nodeCount: project.nodes.length,
    edgeCount: project.edges.length,
    
    // Content sections
    architecture: { nodes: architectureNodes },
    modules,
    destinations,
    
    // Link to interactive version
    shareLink: reportDetails.shareLink || project.shareLink
  };
};

/**
 * Generates a PDF report for a project
 * @param project The project data
 * @param reportDetails The report details
 * @param diagramImageBase64 Base64 encoded image of the diagram
 * @returns The PDF document
 */
export const generateProjectReport = async (
  project: Project,
  reportDetails: ReportDetails,
  diagramImageBase64?: string
) => {
  // Initialize pdfMake
  const initialized = await initPdfMake();
  if (!initialized) {
    throw new Error('Failed to initialize pdfMake');
  }

  // Prepare the report data
  const reportData = await prepareReportData(project, reportDetails);

  // Create the document definition
  const docDefinition = createReportTemplate(
    reportData,
    diagramImageBase64,
    {
      logoUrl: metarouterLogoDataUrl,
      markUrl: metarouterMarkDataUrl,
      architectUrl: architectLogoDataUrl,
    }
  );

  // Create the PDF document
  return pdfMake.createPdf(docDefinition);
};

/**
 * Downloads a PDF report for a project
 * @param project The project data
 * @param reportDetails The report details
 * @param diagramImageBase64 Base64 encoded image of the diagram
 * @param filename Optional filename for the downloaded PDF (defaults to project title)
 */
export const downloadProjectReport = async (
  project: Project,
  reportDetails: ReportDetails,
  diagramImageBase64?: string,
  filename?: string
): Promise<void> => {
  try {
    // Generate the PDF document
    const pdfDoc = await generateProjectReport(project, reportDetails, diagramImageBase64);
    
    // Download the PDF
    pdfDoc.download(filename || `${reportDetails.title.replace(/\s+/g, '_')}_MetaRouter_Report.pdf`);
    
  } catch (error) {
    console.error('Error generating PDF report:', error);
    throw error;
  }
};

/**
 * Opens a PDF report for a project in a new tab
 * @param project The project data
 * @param reportDetails The report details
 * @param diagramImageBase64 Base64 encoded image of the diagram
 */
export const previewProjectReport = async (
  project: Project,
  reportDetails: ReportDetails,
  diagramImageBase64?: string
): Promise<void> => {
  try { 
    // Generate the PDF document
    const pdfDoc = await generateProjectReport(project, reportDetails, diagramImageBase64);
    
    // Open the PDF in a new tab
    pdfDoc.open();
    
  } catch (error) {
    console.error('Error previewing PDF report:', error);
    throw error;
  }
};
